import { useEffect, useState } from 'react';
import { LogIn, Link2, Sparkles, Users, Settings as SettingsIcon, Clock, Star, AlarmClock } from 'lucide-react';
import { connect } from '../net';
import { useStore } from '../store';
import { RoomCodeInput } from './RoomCodeInput';

function randomCode() {
  return String(Math.floor(1000 + Math.random() * 9000));
}

export function Lobby() {
  const userName = useStore((s) => s.userName);
  const errorMsg = useStore((s) => s.errorMsg);
  const [name, setName] = useState(userName || '');
  const [code, setCode] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [turnTimeLimit, setTurnTimeLimit] = useState<30 | 60 | 90 | 120>(60);
  const [allowJokerInInitial, setAllowJokerInInitial] = useState(true);
  const [timeoutPenalty, setTimeoutPenalty] = useState<1 | 3>(3);

  useEffect(() => {
    const url = new URL(location.href);
    const r = url.searchParams.get('room');
    if (r && /^\d{4}$/.test(r)) setCode(r);
  }, []);

  const effectiveName = (name.trim() || `Player${Math.floor(Math.random() * 9999)}`);

  const go = (roomCode: string, withSettings: boolean) => {
    const url = new URL(location.href);
    url.searchParams.set('room', roomCode);
    history.replaceState(null, '', url.toString());
    setConnecting(true);
    connect(roomCode, effectiveName, withSettings ? { turnTimeLimit, allowJokerInInitialMeld: allowJokerInInitial, timeoutPenalty } : undefined);
  };

  const join = () => {
    if (code.length !== 4) return;
    go(code, false);
  };
  const createNew = () => {
    const c = randomCode();
    setCode(c);
    go(c, true);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-6 overflow-auto">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-emerald-500/20 blur-[120px] rounded-full float" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/20 blur-[120px] rounded-full float" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="relative w-full max-w-md fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-400 blur-xl opacity-50" />
              <Sparkles className="relative w-10 h-10 text-emerald-400" strokeWidth={2.5} />
            </div>
            <h1 className="text-5xl font-black tracking-tight bg-gradient-to-r from-emerald-300 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
              Rummikub
            </h1>
          </div>
          <p className="text-slate-400 text-sm">線上多人拉密</p>
        </div>

        <div className="glass rounded-3xl p-8 shadow-2xl space-y-6">
          {/* 暱稱 */}
          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">暱稱</label>
            <input
              className="w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/30 text-lg transition"
              placeholder="輸入你的暱稱"
              maxLength={16}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* 4 碼房號 */}
          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3 block text-center">房間代碼</label>
            <RoomCodeInput value={code} onChange={setCode} />
          </div>

          {/* 動作按鈕 */}
          {/* 規則設定（建立新房間時生效） */}
          <div className="rounded-xl bg-slate-900/40 border border-slate-700/50 overflow-hidden">
            <button
              onClick={() => setShowSettings((v) => !v)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium hover:bg-slate-800/50"
            >
              <span className="flex items-center gap-2">
                <SettingsIcon className="w-4 h-4 text-slate-400" />
                建立房間規則設定
              </span>
              <span className="text-xs text-slate-500">
                {turnTimeLimit}s · {allowJokerInInitial ? '百搭計' : '百搭不計'} · 罰{timeoutPenalty}張
              </span>
            </button>
            {showSettings && (
              <div className="px-4 pb-4 pt-2 space-y-4 border-t border-slate-800">
                <div>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                    <Clock className="w-3.5 h-3.5" /> 回合時間限制
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {([30, 60, 90, 120] as const).map((s) => (
                      <button
                        key={s}
                        onClick={() => setTurnTimeLimit(s)}
                        className={[
                          'py-2 rounded-lg text-sm font-bold transition',
                          turnTimeLimit === s
                            ? 'bg-emerald-600 text-white shadow-lg'
                            : 'bg-slate-800 hover:bg-slate-700 text-slate-300',
                        ].join(' ')}
                      >
                        {s}s
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                    <Star className="w-3.5 h-3.5" /> 破冰時百搭計分
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setAllowJokerInInitial(true)}
                      className={[
                        'py-2 rounded-lg text-sm font-bold transition',
                        allowJokerInInitial ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800 hover:bg-slate-700 text-slate-300',
                      ].join(' ')}
                    >
                      官方規則（計分）
                    </button>
                    <button
                      onClick={() => setAllowJokerInInitial(false)}
                      className={[
                        'py-2 rounded-lg text-sm font-bold transition',
                        !allowJokerInInitial ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800 hover:bg-slate-700 text-slate-300',
                      ].join(' ')}
                    >
                      嚴格（不計）
                    </button>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                    <AlarmClock className="w-3.5 h-3.5" /> 超時罰抽張數
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setTimeoutPenalty(1)}
                      className={[
                        'py-2 rounded-lg text-sm font-bold transition',
                        timeoutPenalty === 1 ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800 hover:bg-slate-700 text-slate-300',
                      ].join(' ')}
                    >
                      +1 張（寬鬆）
                    </button>
                    <button
                      onClick={() => setTimeoutPenalty(3)}
                      className={[
                        'py-2 rounded-lg text-sm font-bold transition',
                        timeoutPenalty === 3 ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800 hover:bg-slate-700 text-slate-300',
                      ].join(' ')}
                    >
                      +3 張（官方）
                    </button>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  設定僅在「建立新房間」時生效；加入既有房間會沿用該房規則。
                </div>
              </div>
            )}
          </div>

          <div className="pt-2 space-y-3">
            <button
              onClick={join}
              disabled={connecting || code.length !== 4}
              className="btn-glow btn-primary w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {connecting ? (
                <>連線中<span className="animate-pulse">…</span></>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  加入房間
                </>
              )}
            </button>
            <button
              onClick={createNew}
              disabled={connecting}
              className="btn-glow btn-secondary w-full py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Link2 className="w-4 h-4" />
              建立新房間（自動產生代碼）
            </button>
          </div>

          {errorMsg && (
            <div className="bg-red-900/40 border border-red-500/30 text-red-200 px-4 py-2 rounded-xl text-sm">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="text-center mt-6 text-xs text-slate-500 flex items-center justify-center gap-2">
          <Users className="w-3.5 h-3.5" />
          2–4 人線上對局 · 手機 / 平板 / 桌面
        </div>
      </div>
    </div>
  );
}
